import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import {
  createCoupon,
  deactivateCoupon,
  getAdminChannels,
  getAdminCombos,
  getCoupons,
  updateCoupon,
} from "../../../lib/apiClient";
import { getErrorMessage } from "../../../lib/utils";
import { Coupon, PopulatedChannel, PopulatedCombo } from "../../../types";

type CouponFormState = {
  coupon_code: string;
  discount_percentage: number | "";
  is_active: boolean;
  applies_to_all_channels: boolean;
  applies_to_all_combos: boolean;
  channel_ids: string[];
  combo_ids: string[];
  valid_for_first_time: boolean;
  valid_for_renewal: boolean;
  valid_for_upgrade: boolean;
};

const emptyForm = (): CouponFormState => ({
  coupon_code: "",
  discount_percentage: "",
  is_active: true,
  applies_to_all_channels: true,
  applies_to_all_combos: true,
  channel_ids: [],
  combo_ids: [],
  valid_for_first_time: true,
  valid_for_renewal: true,
  valid_for_upgrade: true,
});

type TargetOption = {
  _id: string;
  name: string;
};

type TargetSelectorProps = {
  title: string;
  items: TargetOption[];
  selectedIds: string[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onToggle: (id: string) => void;
  applyToAll: boolean;
  onApplyToAllChange: (checked: boolean) => void;
  emptyLabel: string;
};

function TargetSelector({
  title,
  items,
  selectedIds,
  searchValue,
  onSearchChange,
  onToggle,
  applyToAll,
  onApplyToAllChange,
  emptyLabel,
}: TargetSelectorProps) {
  const filteredItems = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, searchValue]);

  return (
    <div className="rounded-lg border border-dark-border/70 bg-dark-primary/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-text-primary">{title}</div>
        <span className="text-xs text-text-secondary">
          {applyToAll ? "All selected" : `${selectedIds.length} selected`}
        </span>
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          checked={applyToAll}
          onChange={(e) => onApplyToAllChange(e.target.checked)}
          className="accent-golden-accent"
        />
        Apply to all
      </label>

      <Input
        label={`Search ${title}`}
        name={`${title.toLowerCase()}_search`}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        disabled={applyToAll}
        placeholder="Search by name"
      />

      <div className={`max-h-64 overflow-y-auto space-y-2 pr-1 ${applyToAll ? "opacity-50" : ""}`}>
        {!filteredItems.length ? (
          <p className="text-sm text-text-secondary">{emptyLabel}</p>
        ) : (
          filteredItems.map((item) => {
            const checked = selectedIds.includes(item._id);
            return (
              <label
                key={item._id}
                className="flex items-center justify-between gap-3 rounded-lg border border-dark-border/50 px-3 py-2 text-sm text-text-primary"
              >
                <span className="truncate">{item.name}</span>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={applyToAll}
                  onChange={() => onToggle(item._id)}
                  className="accent-golden-accent"
                />
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function AdminCouponPage() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [channels, setChannels] = useState<PopulatedChannel[]>([]);
  const [combos, setCombos] = useState<PopulatedCombo[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState<string>("");
  const [form, setForm] = useState<CouponFormState>(emptyForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channelSearch, setChannelSearch] = useState("");
  const [comboSearch, setComboSearch] = useState("");

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [couponList, channelList, comboList] = await Promise.all([
        getCoupons(),
        getAdminChannels(),
        getAdminCombos(),
      ]);
      setCoupons(couponList);
      setChannels(channelList);
      setCombos(comboList);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedCoupon = useMemo(
    () => coupons.find((coupon) => coupon._id === selectedCouponId) || null,
    [coupons, selectedCouponId]
  );

  useEffect(() => {
    if (!selectedCoupon) {
      setForm(emptyForm());
      return;
    }

    setForm({
      coupon_code: selectedCoupon.coupon_code,
      discount_percentage: selectedCoupon.discount_percentage,
      is_active: selectedCoupon.is_active,
      applies_to_all_channels: selectedCoupon.applies_to_all_channels !== false,
      applies_to_all_combos: selectedCoupon.applies_to_all_combos !== false,
      channel_ids: selectedCoupon.channel_ids || [],
      combo_ids: selectedCoupon.combo_ids || [],
      valid_for_first_time: selectedCoupon.valid_for_first_time !== false,
      valid_for_renewal: selectedCoupon.valid_for_renewal !== false,
      valid_for_upgrade: selectedCoupon.valid_for_upgrade !== false,
    });
  }, [selectedCoupon]);

  const resetForm = () => {
    setSelectedCouponId("");
    setForm(emptyForm());
    setChannelSearch("");
    setComboSearch("");
  };

  const toggleChannel = (channelId: string) => {
    setForm((prev) => ({
      ...prev,
      channel_ids: prev.channel_ids.includes(channelId)
        ? prev.channel_ids.filter((id) => id !== channelId)
        : [...prev.channel_ids, channelId],
    }));
  };

  const toggleCombo = (comboId: string) => {
    setForm((prev) => ({
      ...prev,
      combo_ids: prev.combo_ids.includes(comboId)
        ? prev.combo_ids.filter((id) => id !== comboId)
        : [...prev.combo_ids, comboId],
    }));
  };

  const handleSave = async () => {
    if (!form.coupon_code.trim()) {
      toast.error("Coupon code is required.");
      return;
    }

    if (form.discount_percentage === "") {
      toast.error("Discount percentage is required.");
      return;
    }

    const numericDiscount = Number(form.discount_percentage);
    if (Number.isNaN(numericDiscount) || numericDiscount < 1 || numericDiscount > 100) {
      toast.error("Discount percentage must be between 1 and 100.");
      return;
    }

    if (!form.applies_to_all_channels && form.channel_ids.length === 0) {
      toast.error("Select at least one channel or apply to all channels.");
      return;
    }

    if (!form.applies_to_all_combos && form.combo_ids.length === 0) {
      toast.error("Select at least one combo or apply to all combos.");
      return;
    }

    if (!form.valid_for_first_time && !form.valid_for_renewal && !form.valid_for_upgrade) {
      toast.error("Select at least one coupon condition.");
      return;
    }

    const payload = {
      coupon_code: form.coupon_code.trim().toUpperCase(),
      discount_percentage: numericDiscount,
      is_active: form.is_active,
      applies_to_all_channels: form.applies_to_all_channels,
      applies_to_all_combos: form.applies_to_all_combos,
      channel_ids: form.applies_to_all_channels ? [] : form.channel_ids,
      combo_ids: form.applies_to_all_combos ? [] : form.combo_ids,
      valid_for_first_time: form.valid_for_first_time,
      valid_for_renewal: form.valid_for_renewal,
      valid_for_upgrade: form.valid_for_upgrade,
    };

    setIsSaving(true);
    try {
      if (selectedCouponId) {
        await updateCoupon(selectedCouponId, payload);
        toast.success("Coupon updated successfully.");
      } else {
        await createCoupon(payload);
        toast.success("Coupon created successfully.");
      }
      await loadData();
      resetForm();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (couponId: string) => {
    setIsDisabling(true);
    try {
      await deactivateCoupon(couponId);
      toast.success("Coupon deactivated.");
      await loadData();
      if (selectedCouponId === couponId) {
        resetForm();
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setIsDisabling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle
          title="Coupons"
          subtitle="Create multiple coupons and target them to selected channels and combos"
        />
        <div className="flex justify-center items-center py-10">
          <LoadingIndicator size="lg" text="Loading coupons..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 md:space-y-8">
        <PageTitle
          title="Coupons"
          subtitle="Create multiple coupons and target them to selected channels and combos"
        />
        <ErrorMessage title="Failed to load coupons" message={error} />
        <Button onClick={loadData}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      <PageTitle
        title="Coupons"
        subtitle="Create multiple coupons and target them to selected channels and combos"
      />

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              {selectedCouponId ? "Edit Coupon" : "Create Coupon"}
            </h2>
            {selectedCouponId ? (
              <Button variant="secondary" onClick={resetForm} disabled={isSaving || isDisabling}>
                New Coupon
              </Button>
            ) : null}
          </div>

          <Input
            label="Coupon Code"
            name="coupon_code"
            value={form.coupon_code}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, coupon_code: e.target.value.toUpperCase() }))
            }
            placeholder="ENTERCOUPON"
            maxLength={32}
            required
          />

          <Input
            label="Discount Percentage"
            name="discount_percentage"
            type="number"
            value={form.discount_percentage}
            onChange={(e) => {
              const next = e.target.value;
              setForm((prev) => ({
                ...prev,
                discount_percentage: next === "" ? "" : Number(next),
              }));
            }}
            min={1}
            max={100}
            required
          />

          <label className="inline-flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="accent-golden-accent"
            />
            Keep coupon active
          </label>

          <div className="rounded-lg border border-dark-border/70 bg-dark-primary/50 p-4 space-y-3">
            <div className="text-sm font-medium text-text-primary">Coupon Conditions</div>
            <div className="flex flex-wrap gap-4 text-sm text-text-primary">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.valid_for_first_time}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, valid_for_first_time: e.target.checked }))
                  }
                  className="accent-golden-accent"
                />
                First-time subscriptions
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.valid_for_renewal}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, valid_for_renewal: e.target.checked }))
                  }
                  className="accent-golden-accent"
                />
                Renewals
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.valid_for_upgrade}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, valid_for_upgrade: e.target.checked }))
                  }
                  className="accent-golden-accent"
                />
                Upgrades
              </label>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <TargetSelector
              title="Channels"
              items={channels.map((channel) => ({ _id: channel._id, name: channel.name }))}
              selectedIds={form.channel_ids}
              searchValue={channelSearch}
              onSearchChange={setChannelSearch}
              onToggle={toggleChannel}
              applyToAll={form.applies_to_all_channels}
              onApplyToAllChange={(checked) =>
                setForm((prev) => ({
                  ...prev,
                  applies_to_all_channels: checked,
                  channel_ids: checked ? [] : prev.channel_ids,
                }))
              }
              emptyLabel="No channels match this search."
            />

            <TargetSelector
              title="Combos"
              items={combos.map((combo) => ({ _id: combo._id, name: combo.name }))}
              selectedIds={form.combo_ids}
              searchValue={comboSearch}
              onSearchChange={setComboSearch}
              onToggle={toggleCombo}
              applyToAll={form.applies_to_all_combos}
              onApplyToAllChange={(checked) =>
                setForm((prev) => ({
                  ...prev,
                  applies_to_all_combos: checked,
                  combo_ids: checked ? [] : prev.combo_ids,
                }))
              }
              emptyLabel="No combos match this search."
            />
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={handleSave} isLoading={isSaving} disabled={isDisabling}>
              {selectedCouponId ? "Update Coupon" : "Create Coupon"}
            </Button>
            <Button variant="secondary" onClick={loadData} disabled={isSaving || isDisabling}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-xl rounded-xl border border-dark-border/50 p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-text-primary">Existing Coupons</h2>

          {coupons.length === 0 ? (
            <p className="text-sm text-text-secondary">No coupons created yet.</p>
          ) : (
            <div className="space-y-3">
              {coupons.map((coupon) => (
                <div
                  key={coupon._id}
                  className="rounded-lg border border-dark-border/70 bg-dark-primary/50 p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-text-primary">{coupon.coupon_code}</div>
                      <div className="text-sm text-text-secondary">
                        {coupon.discount_percentage}% off
                        {coupon.is_active ? " • Active" : " • Inactive"}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedCouponId(coupon._id)}
                        disabled={isSaving || isDisabling}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => handleDeactivate(coupon._id)}
                        isLoading={isDisabling && selectedCouponId === coupon._id}
                        disabled={isSaving || !coupon.is_active}
                      >
                        Deactivate
                      </Button>
                    </div>
                  </div>

                  <div className="text-xs text-text-secondary space-y-1">
                    <div>
                      Channels: {coupon.applies_to_all_channels !== false ? "All" : (coupon.channel_ids || []).join(", ") || "None"}
                    </div>
                    <div>
                      Combos: {coupon.applies_to_all_combos !== false ? "All" : (coupon.combo_ids || []).join(", ") || "None"}
                    </div>
                    <div>
                      Conditions: {[
                        coupon.valid_for_first_time !== false ? "First-time" : null,
                        coupon.valid_for_renewal !== false ? "Renewal" : null,
                        coupon.valid_for_upgrade !== false ? "Upgrade" : null,
                      ].filter(Boolean).join(", ") || "None"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
